/** Major section boundary -- mirrors studioTeamState.ts's MAJOR_SECTION_RE. */
const MAJOR_SECTION_RE = /^\[[^\]]*\b(?:Intro|Outro|Verse|Chorus|Bridge|Break|Solo)\b[^\]]*\]\s*$/i;
const ONLY_BRACKETS_RE = /^(?:[[{][^\]}]*[\]}]\s*)+$/;
const ONLY_PAREN_RE = /^\([^)]*\)$/;
const BLEED_TOKEN_LINE_RE = /^\/\/\/\*+\/\/\/$/;

function isRealLyricLine(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  if (ONLY_BRACKETS_RE.test(t)) return false;
  if (ONLY_PAREN_RE.test(t)) return false;
  if (BLEED_TOKEN_LINE_RE.test(t)) return false;
  // Bare production directives ("NO VOCALS", "NO WORDLESS VOCALS") are
  // written as plain all-caps lines, not bracketed -- catch those too.
  if (t === t.toUpperCase() && /^NO\s+VOCALS\b/i.test(t)) return false;
  return true;
}

/**
 * Deterministic last-resort trim for a lyrics body that ended up over a
 * hard character ceiling -- drops trailing lyrical lines one at a time,
 * never a [Section]/{Header} tag, never the metadata bleed token line,
 * until under a target max. A blind substring/character chop can land
 * mid-word or mid-sentence and silently drop an entire trailing structural
 * block (an outro/fade direction, say) -- this always drops on a line
 * boundary.
 *
 * Also protects each major section's LAST remaining real lyric line.
 * Forgejo (2026-09-12, "Glass Room in the Crowd"): a song whose trailing
 * section was a long bracket-only instrumental solo had almost no
 * deletable filler near the tail, so the walk kept marching backward
 * through the ENTIRE body -- deleting every sung line in every verse and
 * chorus, front to back -- to reclaim enough characters. The result had
 * full section-tag scaffolding and zero actual lyrics. This mirrors the
 * section-aware protection already proven in
 * server/services/studioTeamState.ts's trimLyricsToFit (commit eb57828),
 * which this module should have reused from the start instead of
 * duplicating the unprotected walk.
 *
 * Shared between Studio Team's server-side pipeline
 * (server/routes/studioteam.routes.ts's enforceLyricsHardCap, which finds
 * this body inside a larger sentinel-delimited multi-block response) and
 * RiffWriter's client-side pipeline (App.tsx, where lyricsWithTags already
 * IS the isolated body). Same risk in both: post-processing that runs
 * after the LLM responds (Riff DNA prepend, physics-layer injection, a
 * trim/expand pass) can push an already-acceptable body over the ceiling
 * with nothing downstream to catch it.
 */
export function trimLyricsBodyToHardCap(
  body: string,
  absMax: number,
  targetMax: number,
): { body: string; finalChars: number; hardTrimmed: boolean } {
  if (body.length <= absMax) return { body, finalChars: body.length, hardTrimmed: false };

  const lines = body.split('\n');

  // Map each line to its enclosing major section and count each section's
  // real lyric lines, so the walk below can refuse to delete a section's
  // LAST remaining real line -- gutting a verse/chorus to nothing is worse
  // than staying slightly over budget.
  const sectionOfLine: (number | null)[] = new Array(lines.length).fill(null);
  const realLineCountBySection = new Map<number, number>();
  let currentSection: number | null = null;
  let sectionCounter = -1;
  lines.forEach((line, i) => {
    if (MAJOR_SECTION_RE.test(line.trim())) {
      sectionCounter += 1;
      currentSection = sectionCounter;
      realLineCountBySection.set(currentSection, 0);
      return; // the tag line itself has no section membership of its own
    }
    sectionOfLine[i] = currentSection;
    if (currentSection !== null && isRealLyricLine(line)) {
      realLineCountBySection.set(currentSection, (realLineCountBySection.get(currentSection) ?? 0) + 1);
    }
  });

  // Walk backwards dropping eligible lines until under targetMax
  for (let i = lines.length - 1; i >= 0 && lines.join('\n').length > targetMax; i--) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) { lines.splice(i, 1); continue; }
    // Preserve [Section] tags and {Header} lines
    if (/^\[[^\]]+\]$/.test(line) || /^\{[^}]+\}$/.test(line)) continue;
    // Preserve the metadata bleed token
    if (/^\/\/\/\*+\/\/\//.test(line)) continue;
    const sec = sectionOfLine[i];
    if (sec !== null && isRealLyricLine(raw) && (realLineCountBySection.get(sec) ?? 0) <= 1) {
      continue; // this section's last real line -- protect it
    }
    if (sec !== null && isRealLyricLine(raw)) {
      realLineCountBySection.set(sec, (realLineCountBySection.get(sec) ?? 0) - 1);
    }
    // Drop this line
    lines.splice(i, 1);
  }
  const trimmed = lines.join('\n').trimEnd();
  return { body: trimmed, finalChars: trimmed.length, hardTrimmed: true };
}
